import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Building2, Plus, Trash2, X, Pencil, ExternalLink, CircleDot, Mail, TrendingUp, ArrowUpRight, ArrowDownRight, Users, LogIn, AlertTriangle, CheckCircle2, Link2 } from "lucide-react";
import SharedDateField from "@/components/app/DateField";

export const PLAN_INFO = {
  presencia:  { label: "Presencia",  color: "text-muted-foreground" },
  club:       { label: "Club",       color: "text-primary" },
  total:      { label: "Total",      color: "text-emerald-400" },
  familia:    { label: "Player",     color: "text-sky-400" },
};

const PLAN_STATUS = {
  trial:     { label: "Prueba",    color: "text-amber-400" },
  active:    { label: "Activo",    color: "text-emerald-400" },
  suspended: { label: "Suspendido",color: "text-destructive" },
  cancelled: { label: "Cancelado", color: "text-muted-foreground" },
};

function slugify(text) {
  const map = { á:"a", é:"e", í:"i", ó:"o", ú:"u", ñ:"n", ü:"u", à:"a", è:"e", ç:"c" };
  return (text || "")
    .toLowerCase()
    .split("").map(c => map[c] || c).join("")
    .replace(/[^a-z0-9-]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 40);
}

export default function SuperAdmin() {
  const nav = useNavigate();
  const { refresh, user } = useAuth();
  const [clubs, setClubs] = useState([]);
  const [mrr, setMrr] = useState(null);
  const [revenue, setRevenue] = useState(null);
  const [showAllRenewals, setShowAllRenewals] = useState(false);
  const [emailStats, setEmailStats] = useState(null);
  const [showFailures, setShowFailures] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [impersonatingId, setImpersonatingId] = useState(null);
  const [tab, setTab] = useState("clubes");
  const [form, setForm] = useState(emptyForm());

  function emptyForm() {
    return {
      name: "", subdomain: "", subdomainTouched: false, city: "", contact_email: "", contact_phone: "",
      plan: "total", plan_status: "trial", plan_expires_at: "", seats: 20,
      admin_name: "", admin_email: "", admin_password: "",
    };
  }

  const [accessCreds, setAccessCreds] = useState(null);
  const [fQ, setFQ] = useState("");
  const [fStatus, setFStatus] = useState("todos");
  const [fPlan, setFPlan] = useState("todos");
  const [fDateField, setFDateField] = useState("alta");
  const [fDesde, setFDesde] = useState("");
  const [fHasta, setFHasta] = useState("");
  const [fSort, setFSort] = useState("recientes");

  const adminAccess = async (club, email = null) => {
    try {
      const { data } = await api.post(`/clubs/${club.id}/admin-access`, email ? { email } : {});
      setAccessCreds({ ...data, clubName: club.name });
    } catch (e) {
      const detail = e.response?.data?.detail || "";
      if (String(detail).includes("Indica el email")) {
        const em = window.prompt(`"${club.name}" no tiene usuario admin todavía.\n\nEscribe el email del administrador para crearle el acceso:`);
        if (em && em.includes("@")) adminAccess(club, em.trim());
      } else {
        toast.error(formatApiError(detail) || "No se pudo generar el acceso");
      }
    }
  };

  const impersonate = async (club) => {
    if (!window.confirm(
      `Vas a entrar en "${club.name}" como su administrador.\n\n` +
      `Todas las acciones que realices quedarán registradas en el historial ` +
      `como impersonación desde tu cuenta de Super Admin.\n\nPodrás volver ` +
      `atrás desde el botón "Volver a Super Admin" en la barra superior.`
    )) return;
    setImpersonatingId(club.id);
    try {
      const { data } = await api.post(`/clubs/${club.id}/impersonate`);
      const currentToken = localStorage.getItem("access_token");
      localStorage.setItem("super_token_backup", currentToken);
      localStorage.setItem("access_token", data.access_token);
      await refresh();
      toast.success(`Entrando en ${club.name}…`);
      if (data.user.role === "club_admin") nav("/club");
      else if (data.user.role === "coordinator") nav("/coordinator");
      else nav("/coach");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setImpersonatingId(null);
    }
  };

  const markPaid = async (club) => {
    const planOptions = { p: "presencia", c: "club", t: "total" };
    const planChoice = window.prompt(
      `Activar "${club.name}" como PAGADO POR ADELANTADO (sin cobro por Stripe).\n\n` +
      `¿Qué plan? Escribe una letra:\n` +
      `  p = Presencia\n  c = Club\n  t = Total\n\n` +
      `(Pulsa Cancelar para abortar)`,
      "c"
    );
    if (!planChoice) return;
    const plan = planOptions[planChoice.toLowerCase().trim()];
    if (!plan) { toast.error("Plan inválido. Usa p, c o t."); return; }
    const months = window.prompt(
      `¿Cuántos MESES pagados por adelantado?\n\nEj: 12 = un año, 6 = medio año.`,
      "12"
    );
    const nMonths = parseInt(months, 10);
    if (!nMonths || nMonths <= 0 || nMonths > 120) { toast.error("Número de meses inválido"); return; }
    const expiresAt = new Date();
    expiresAt.setMonth(expiresAt.getMonth() + nMonths);
    const iso = expiresAt.toISOString().slice(0, 10);
    if (!window.confirm(
      `Se activará el club "${club.name}" con:\n\n` +
      `  Plan: ${plan.toUpperCase()}\n` +
      `  Estado: ACTIVO\n` +
      `  Vence: ${iso} (${nMonths} meses desde hoy)\n\n` +
      `NO se cobrará nada — se registra que ya pagaron por adelantado.\n\n¿Confirmar?`
    )) return;
    try {
      await api.put(`/clubs/${club.id}`, {
        plan,
        plan_status: "active",
        plan_expires_at: iso,
      });
      toast.success(`${club.name} activado hasta ${iso}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: clubList }, mrrRes, emailRes, revRes] = await Promise.all([
        api.get("/clubs"),
        api.get("/billing/mrr").catch(() => ({ data: null })),
        api.get("/admin/email-stats?days=30").catch(() => ({ data: null })),
        api.get("/billing/revenue").catch(() => ({ data: null })),
      ]);
      setClubs(clubList);
      setMrr(mrrRes?.data || null);
      setEmailStats(emailRes?.data || null);
      setRevenue(revRes?.data || null);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(emptyForm()); setShowForm(true); };
  const openEdit = (c) => {
    setEditing(c);
    setForm({
      name: c.name || "", subdomain: c.subdomain || "", subdomainTouched: true,
      city: c.city || "", contact_email: c.contact_email || "", contact_phone: c.contact_phone || "",
      plan: c.plan || "club", plan_status: c.plan_status || "trial",
      plan_expires_at: c.plan_expires_at || "", seats: c.seats || 20,
      admin_name: "", admin_email: "", admin_password: "",
    });
    setShowForm(true);
  };

  const resendKit = async (c) => {
    const reset = confirm(
      `¿Quieres restablecer la contraseña del administrador de ${c.name}?\n\n` +
      `Aceptar = generar nueva contraseña y enviarla en el email.\n` +
      `Cancelar = enviar el kit sin cambiar la contraseña.`
    );
    let newPass = null;
    if (reset) {
      newPass = prompt("Nueva contraseña (mínimo 8 caracteres):", "Cambiame123!");
      if (!newPass) return;
    }
    try {
      const body = newPass ? { new_password: newPass } : {};
      const params = newPass ? `?new_password=${encodeURIComponent(newPass)}` : "";
      await api.post(`/clubs/${c.id}/resend-welcome${params}`, body);
      toast.success(newPass ? "Kit enviado con nueva contraseña" : "Kit reenviado al administrador");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const paymentLink = async (c) => {
    const plan = window.prompt(
      `Enlace de pago para ${c.name}\n\nEscribe el plan: presencia / club / total`,
      c.plan && c.plan !== "familia" ? c.plan : "club"
    );
    if (!plan) return;
    const p = plan.trim().toLowerCase();
    if (!["presencia", "club", "total"].includes(p)) return toast.error("Plan inválido — escribe: presencia, club o total");
    const yearly = window.confirm("Aceptar = pago ANUAL (12 meses al precio de 11)\nCancelar = pago MENSUAL");
    try {
      const { data } = await api.post("/billing/payment-link", { club_id: c.id, plan: p, interval: yearly ? "year" : "month" });
      try { await navigator.clipboard.writeText(data.checkout_url); } catch { /* fallback below */ }
      window.prompt("Enlace de pago (ya copiado al portapapeles — pégalo en WhatsApp). Caduca en 24h:", data.checkout_url);
      toast.success(`Enlace generado · ${data.amount} ${(data.currency || "eur").toUpperCase()} — caduca en 24h`, { duration: 9000 });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo generar el enlace");
    }
  };

  const setName = (v) => setForm(f => ({ ...f, name: v, subdomain: f.subdomainTouched ? f.subdomain : slugify(v) }));
  const setSubdomain = (v) => setForm(f => ({ ...f, subdomain: v.toLowerCase(), subdomainTouched: true }));

  const submit = async (e) => {
    e.preventDefault();
    const payload = { ...form };
    delete payload.subdomainTouched;
    Object.keys(payload).forEach((k) => { if (payload[k] === "") payload[k] = null; });
    payload.seats = form.seats ? Number(form.seats) : null;
    try {
      if (editing) {
        const { admin_name, admin_email, admin_password, ...upd } = payload;
        await api.put(`/clubs/${editing.id}`, upd);
        toast.success("Club actualizado");
      } else {
        await api.post("/clubs", payload);
        toast.success("Club creado");
      }
      setShowForm(false);
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar este club y TODOS sus datos? Esta acción es irreversible.")) return;
    try {
      await api.delete(`/clubs/${id}`);
      toast.success("Club eliminado");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const clubAccounts = clubs.filter((c) => c.plan !== "familia");
  const playerAccounts = clubs.filter((c) => c.plan === "familia");
  const today = new Date().toISOString().slice(0, 10);
  const isVencidoClub = (c) => !!c.plan_expires_at && c.plan_expires_at.slice(0, 10) < today && !["cancelled", "suspended"].includes(c.plan_status);
  const norm = (s) => (s || "").toString().toLowerCase();
  const baseList = tab === "player" ? playerAccounts : clubAccounts;
  const visible = baseList.filter((c) => {
    if (fQ.trim()) {
      const hay = [c.name, c.subdomain, c.city, c.contact_email, c.contact_phone, c.billing_email].map(norm).join(" ");
      if (!hay.includes(norm(fQ.trim()))) return false;
    }
    if (fStatus === "vencido") { if (!isVencidoClub(c)) return false; }
    else if (fStatus !== "todos" && c.plan_status !== fStatus) return false;
    if (tab === "clubes" && fPlan !== "todos" && c.plan !== fPlan) return false;
    const dv = ((fDateField === "alta" ? c.created_at : c.plan_expires_at) || "").slice(0, 10);
    if (fDesde && (!dv || dv < fDesde)) return false;
    if (fHasta && (!dv || dv > fHasta)) return false;
    return true;
  }).sort((a, b) => {
    if (fSort === "nombre") return (a.name || "").localeCompare(b.name || "");
    if (fSort === "vencimiento") return (a.plan_expires_at || "9999").localeCompare(b.plan_expires_at || "9999");
    return (b.created_at || "").localeCompare(a.created_at || "");
  });
  const filtersActive = fQ.trim() || fStatus !== "todos" || fPlan !== "todos" || fDesde || fHasta;
  const clearFilters = () => { setFQ(""); setFStatus("todos"); setFPlan("todos"); setFDesde(""); setFHasta(""); };
  const exportFiltered = () => {
    const esc = (v) => `"${(v ?? "").toString().replace(/"/g, '""')}"`;
    const header = ["Nombre", "Subdominio", "Ciudad", "Email contacto", "Teléfono", "Plan", "Estado", "Alta", "Vencimiento"];
    const rows = visible.map((c) => [
      c.name, c.subdomain, c.city, c.contact_email, c.contact_phone, c.plan,
      fStatus === "vencido" || isVencidoClub(c) ? `${c.plan_status} (vencido)` : c.plan_status,
      (c.created_at || "").slice(0, 10), (c.plan_expires_at || "").slice(0, 10),
    ]);
    const csv = "\ufeff" + [header, ...rows].map((r) => r.map(esc).join(";")).join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `clubes-filtrados-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
    toast.success(`Exportados ${visible.length} ${tab === "player" ? "registros" : "clubes"} (abre con Excel)`);
  };
  const mrrLine = mrr ? ((tab === "player" ? mrr.player : mrr.clubes) || mrr) : null;

  return (
    <div>
      {accessCreds && (
        <div className="fixed inset-0 z-[80] bg-black/75 flex items-center justify-center p-4" onClick={() => setAccessCreds(null)}>
          <div className="w-full max-w-md bg-card border-2 border-amber-500 p-6" onClick={(e) => e.stopPropagation()} data-testid="admin-access-modal">
            <div className="overline text-amber-400">🔑 Acceso de administrador · {accessCreds.clubName}</div>
            <div className="font-heading font-black text-xl uppercase mt-1">{accessCreds.created ? "Usuario creado" : "Contraseña reseteada"}</div>
            <div className="mt-4 space-y-2 font-mono text-sm">
              <div className="flex items-center justify-between bg-muted/40 border border-border px-3 py-2">
                <span data-testid="access-email">{accessCreds.email}</span>
              </div>
              <div className="flex items-center justify-between bg-muted/40 border border-border px-3 py-2">
                <span data-testid="access-password" className="font-bold text-amber-400">{accessCreds.password}</span>
              </div>
            </div>
            <p className="text-[11px] text-muted-foreground mt-3">
              ⚠️ Esta contraseña solo se muestra ahora — cópiala y pásasela al club. La anterior ya no funciona.
              {accessCreds.email_sent ? " 📧 El club también la ha recibido por email." : " (No se pudo enviar el email automático: pásasela tú.)"}
            </p>
            <div className="flex gap-2 mt-4">
              <button
                data-testid="access-copy-btn"
                onClick={() => {
                  navigator.clipboard.writeText(`Acceso Sudefootball · ${accessCreds.clubName}\nURL: https://app.sudefootball.sudeporte.com/login\nEmail: ${accessCreds.email}\nContraseña: ${accessCreds.password}`);
                  toast.success("Credenciales copiadas");
                }}
                className="flex-1 h-10 bg-amber-500 text-black font-heading text-xs uppercase tracking-widest hover:opacity-90"
              >
                Copiar credenciales
              </button>
              <button data-testid="access-close-btn" onClick={() => setAccessCreds(null)} className="h-10 px-4 border border-border font-heading text-xs uppercase tracking-widest">Cerrar</button>
            </div>
          </div>
        </div>
      )}
      <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
        <div>
          <div className="overline">Panel de plataforma · Sudefootball</div>
          <h1 className="font-heading font-black text-5xl md:text-6xl tracking-tighter uppercase mt-2">{tab === "player" ? "Player" : tab === "equipo" ? "Equipo" : "Clubes"}</h1>
          <p className="text-muted-foreground text-sm mt-2 max-w-lg">
            {tab === "player"
              ? "Familias suscritas al plan Sudefootball Player (B2C). Se dan de alta solas desde /player."
              : tab === "equipo"
              ? "Usuarios de empresa y altas de usuarios en cualquier club, con su rol y cargo."
              : "Alta manual de clubes, planes de suscripción e identificador interno del club."}
          </p>
        </div>
        {tab === "clubes" && (
          <button data-testid="create-club-btn" onClick={openCreate} className="btn-volt">
            <Plus size={16} /> Nuevo club
          </button>
        )}
      </div>

      <div className="flex gap-2 mb-6">
        <button data-testid="superadmin-tab-clubes" onClick={() => setTab("clubes")}
          className={`h-11 px-5 font-heading font-black uppercase text-xs tracking-wider border-2 transition-colors ${tab === "clubes" ? "border-primary text-primary bg-primary/10" : "border-border text-muted-foreground hover:border-primary/50"}`}>
          🏟 Clubes ({clubAccounts.length})
        </button>
        <button data-testid="superadmin-tab-player" onClick={() => setTab("player")}
          className={`h-11 px-5 font-heading font-black uppercase text-xs tracking-wider border-2 transition-colors ${tab === "player" ? "border-sky-400 text-sky-400 bg-sky-400/10" : "border-border text-muted-foreground hover:border-sky-400/50"}`}>
          ⚽ Player ({playerAccounts.length})
        </button>
        {user?.role === "super_admin" && (
          <button data-testid="superadmin-tab-equipo" onClick={() => setTab("equipo")}
            className={`h-11 px-5 font-heading font-black uppercase text-xs tracking-wider border-2 transition-colors ${tab === "equipo" ? "border-red-400 text-red-400 bg-red-400/10" : "border-border text-muted-foreground hover:border-red-400/50"}`}>
            👥 Equipo
          </button>
        )}
      </div>

      {tab === "equipo" ? null : tab === "clubes" ? (
        <div className="grid grid-tactical grid-cols-2 md:grid-cols-4 mb-6">
          <SummaryStat label="Clubes totales" value={clubAccounts.length} />
          <SummaryStat label="Activos" value={clubAccounts.filter(c => c.plan_status === "active").length} />
          <SummaryStat label="En prueba" value={clubAccounts.filter(c => c.plan_status === "trial").length} />
          <SummaryStat label="Suspendidos" value={clubAccounts.filter(c => c.plan_status === "suspended").length} />
        </div>
      ) : (
        <div className="grid grid-tactical grid-cols-2 md:grid-cols-4 mb-6">
          <SummaryStat label="Familias totales" value={playerAccounts.length} />
          <SummaryStat label="Activas" value={playerAccounts.filter(c => c.plan_status === "active").length} />
          <SummaryStat label="Hijos seguidos" value={playerAccounts.reduce((a, c) => a + (Number(c.seats) || 0), 0)} />
          <SummaryStat label="Suspendidas" value={playerAccounts.filter(c => c.plan_status === "suspended").length} />
        </div>
      )}

      {tab === "clubes" && (mrr || revenue) && (
        <div className="border-2 border-emerald-400/30 bg-emerald-400/[0.03] p-5 mb-8" data-testid="revenue-panel">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp size={16} className="text-emerald-400"/>
            <span className="overline text-emerald-400">Negocio · {revenue?.month || ""}</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div data-testid="revenue-mrr">
              <div className="overline">MRR (ingreso mensual recurrente)</div>
              <div className="font-heading font-black text-3xl tracking-tighter mt-1 text-emerald-400">
                {(mrr?.mrr ?? 0).toFixed(2)} €
              </div>
              <div className="text-[10px] text-muted-foreground mt-1">
                Clubes {(mrr?.clubes?.mrr ?? 0).toFixed(2)} € · Player {(mrr?.player?.mrr ?? 0).toFixed(2)} €
              </div>
            </div>
            <div data-testid="revenue-month-income">
              <div className="overline">Cobrado este mes</div>
              <div className="font-heading font-black text-3xl tracking-tighter mt-1">
                {(revenue?.ingresos_mes ?? 0).toFixed(2)} €
              </div>
              <div className="text-[10px] text-muted-foreground mt-1">{revenue?.num_cobros ?? 0} cobro(s) en Stripe</div>
            </div>
            <div data-testid="revenue-active-subs">
              <div className="overline">Suscripciones activas</div>
              <div className="font-heading font-black text-3xl tracking-tighter mt-1">{mrr?.active_subscriptions ?? 0}</div>
            </div>
            <div data-testid="revenue-churn">
              <div className="overline">Altas / Bajas del mes</div>
              <div className="font-heading font-black text-3xl tracking-tighter mt-1 flex items-center gap-3">
                <span className="text-emerald-400 flex items-center gap-1"><ArrowUpRight size={18}/>{mrr?.altas_mes ?? 0}</span>
                <span className="text-red-400 flex items-center gap-1"><ArrowDownRight size={18}/>{mrr?.bajas_mes ?? 0}</span>
              </div>
            </div>
          </div>
          {revenue?.renovaciones?.length > 0 && (
            <div className="mt-5">
              <div className="overline mb-2">Próximas renovaciones</div>
              <div className="border border-emerald-400/20 overflow-x-auto" data-testid="renewals-table">
                <table className="w-full text-xs">
                  <thead className="bg-card/50">
                    <tr className="text-[9px] uppercase tracking-wider text-muted-foreground">
                      <th className="text-left px-3 py-2">Fecha</th>
                      <th className="text-left px-3 py-2">Cuenta</th>
                      <th className="text-left px-3 py-2">Plan</th>
                      <th className="text-right px-3 py-2">Importe</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(showAllRenewals ? revenue.renovaciones : revenue.renovaciones.slice(0, 6)).map((r, i) => (
                      <tr key={i} className="border-t border-border">
                        <td className="px-3 py-2 font-mono text-[10px]">{r.fecha}</td>
                        <td className="px-3 py-2">{r.cuenta}<span className="text-muted-foreground text-[10px]"> · {r.email || ""}</span></td>
                        <td className={`px-3 py-2 text-[10px] uppercase font-heading ${PLAN_INFO[r.plan]?.color || ""}`}>{PLAN_INFO[r.plan]?.label || r.plan}</td>
                        <td className="px-3 py-2 text-right font-mono">{r.importe.toFixed(2)} €<span className="text-muted-foreground text-[10px]">/{r.intervalo === "year" ? "año" : "mes"}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {revenue.renovaciones.length > 6 && (
                <button data-testid="renewals-toggle-btn" onClick={() => setShowAllRenewals(!showAllRenewals)}
                  className="mt-2 text-[10px] font-heading uppercase tracking-wider text-emerald-400 hover:text-emerald-300 transition-colors">
                  {showAllRenewals ? "▲ Ver menos" : `▼ Ver las ${revenue.renovaciones.length}`}
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {tab === "clubes" && emailStats && (
        <div className="border-2 border-primary/30 bg-primary/[0.03] p-5 mb-8" data-testid="email-stats-panel">
          <div className="flex items-center gap-2 mb-4">
            <Mail size={16} className="text-primary"/>
            <span className="overline text-primary">Entrega de emails · últimos {emailStats.window_days} días</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div data-testid="email-total-sent">
              <div className="overline">Enviados</div>
              <div className="font-heading font-black text-3xl tracking-tighter mt-1 flex items-center gap-2">
                <CheckCircle2 size={20} className="text-emerald-400"/>{emailStats.total_sent}
              </div>
            </div>
            <div data-testid="email-total-failed">
              <div className="overline">Fallidos</div>
              <div className={`font-heading font-black text-3xl tracking-tighter mt-1 flex items-center gap-2 ${emailStats.total_failed > 0 ? "text-red-400" : "text-muted-foreground"}`}>
                <AlertTriangle size={20}/>{emailStats.total_failed}
              </div>
            </div>
            <div data-testid="email-success-rate">
              <div className="overline">% Éxito</div>
              <div className={`font-heading font-black text-3xl tracking-tighter mt-1 ${emailStats.success_rate >= 95 ? "text-emerald-400" : emailStats.success_rate >= 80 ? "text-amber-400" : "text-red-400"}`}>
                {emailStats.success_rate}%
              </div>
            </div>
            <div>
              <div className="overline">Últimas 6 semanas</div>
              <div className="flex items-end gap-1 mt-2 h-10">
                {(emailStats.weekly || []).slice(-6).map((w) => {
                  const total = w.sent + w.failed;
                  const okPct = total ? (w.sent / total) * 100 : 0;
                  const barH = Math.min(40, 4 + total * 2);
                  return (
                    <div key={w.week} className="flex-1 flex flex-col justify-end" title={`${w.week}: ${w.sent} ok · ${w.failed} fail`}>
                      <div className="w-full bg-red-400/40" style={{ height: `${barH * (1 - okPct / 100)}px` }} />
                      <div className="w-full bg-emerald-400/70" style={{ height: `${barH * okPct / 100}px` }} />
                    </div>
                  );
                })}
                {(emailStats.weekly || []).length === 0 && (
                  <div className="text-[10px] text-muted-foreground">Sin datos</div>
                )}
              </div>
            </div>
          </div>
          {emailStats.recent_failures?.length > 0 && (
            <button
              data-testid="email-toggle-failures-btn"
              onClick={() => setShowFailures(!showFailures)}
              className="mt-4 text-[10px] font-heading uppercase tracking-wider text-red-400 hover:text-red-300 transition-colors">
              {showFailures ? "▲ Ocultar" : "▼ Ver"} {emailStats.recent_failures.length} fallos recientes
            </button>
          )}
          {showFailures && emailStats.recent_failures?.length > 0 && (
            <div className="mt-3 border border-red-400/30 max-h-64 overflow-y-auto" data-testid="email-failures-list">
              <table className="w-full text-xs">
                <thead className="bg-card/50 sticky top-0">
                  <tr className="text-[9px] uppercase tracking-wider text-muted-foreground">
                    <th className="text-left px-3 py-2">Fecha</th>
                    <th className="text-left px-3 py-2">Destinatario</th>
                    <th className="text-left px-3 py-2">Asunto</th>
                    <th className="text-left px-3 py-2">Error</th>
                  </tr>
                </thead>
                <tbody>
                  {emailStats.recent_failures.map((f, i) => (
                    <tr key={i} className="border-t border-border">
                      <td className="px-3 py-2 font-mono text-[10px] text-muted-foreground">{(f.created_at || "").slice(0, 16)}</td>
                      <td className="px-3 py-2 font-mono text-[10px]">{f.recipient}</td>
                      <td className="px-3 py-2 text-[10px]">{f.subject}</td>
                      <td className="px-3 py-2 text-[10px] text-red-400">{f.error}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {tab !== "equipo" && mrr && mrrLine && (
        <div className={`border-2 p-5 mb-8 ${tab === "player" ? "border-sky-400/40 bg-sky-400/[0.03]" : "border-emerald-500/40 bg-emerald-500/[0.03]"}`} data-testid="mrr-panel">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp size={16} className={tab === "player" ? "text-sky-400" : "text-emerald-400"}/>
            <span className={`overline ${tab === "player" ? "text-sky-400" : "text-emerald-400"}`}>Ingresos {tab === "player" ? "Player (B2C)" : "Clubes (B2B)"} · {mrr.month}</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div data-testid="mrr-value">
              <div className="overline">MRR</div>
              <div className="font-heading font-black text-3xl tracking-tighter mt-1">
                {new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(mrrLine.mrr)}
              </div>
              <div className="text-[10px] text-muted-foreground mt-1">Neto tras descuentos</div>
            </div>
            <div>
              <div className="overline">Suscripciones activas</div>
              <div className="font-heading font-black text-3xl tracking-tighter mt-1 flex items-center gap-2">
                <Users size={20} className="text-muted-foreground"/> {mrrLine.active_subscriptions}
              </div>
              <div className="text-[10px] text-muted-foreground mt-1">
                {tab === "player" ? `${mrr.by_plan?.familia || 0} familias` : `P ${mrr.by_plan?.presencia || 0} · C ${mrr.by_plan?.club || 0} · T ${mrr.by_plan?.total || 0}`}
              </div>
            </div>
            <div>
              <div className="overline">Altas del mes</div>
              <div className="font-heading font-black text-3xl tracking-tighter mt-1 text-emerald-400 flex items-center gap-2">
                <ArrowUpRight size={20}/> {mrrLine.altas_mes}
              </div>
            </div>
            <div>
              <div className="overline">Bajas del mes</div>
              <div className={`font-heading font-black text-3xl tracking-tighter mt-1 flex items-center gap-2 ${mrrLine.bajas_mes > 0 ? "text-red-400" : "text-muted-foreground"}`}>
                <ArrowDownRight size={20}/> {mrrLine.bajas_mes}
              </div>
            </div>
            <div>
              <div className="overline">Neto del mes</div>
              <div className={`font-heading font-black text-3xl tracking-tighter mt-1 ${(mrrLine.altas_mes - mrrLine.bajas_mes) >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                {(mrrLine.altas_mes - mrrLine.bajas_mes) >= 0 ? "+" : ""}{mrrLine.altas_mes - mrrLine.bajas_mes}
              </div>
            </div>
          </div>
        </div>
      )}

      {tab !== "equipo" && (<>
      <div className="border border-border bg-card/40 p-4 mb-6" data-testid="platform-filters">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[220px]">
            <label className="overline block mb-1">🔍 Buscar</label>
            <input
              data-testid="platform-search-input"
              value={fQ}
              onChange={(e) => setFQ(e.target.value)}
              placeholder="Nombre, email, teléfono, ciudad, subdominio…"
              className="w-full h-10 bg-card border border-border px-3 font-mono text-sm focus:border-primary focus:outline-none"
            />
          </div>
          <div>
            <label className="overline block mb-1">Estado</label>
            <select data-testid="filter-status" value={fStatus} onChange={(e) => setFStatus(e.target.value)}
              className="h-10 bg-card border border-border px-2 font-mono text-xs focus:border-primary focus:outline-none">
              <option value="todos">Todos</option>
              <option value="trial">En prueba</option>
              <option value="active">Activos</option>
              <option value="vencido">⏰ Vencidos (fecha pasada)</option>
              <option value="suspended">Suspendidos</option>
              <option value="cancelled">Cancelados</option>
            </select>
          </div>
          {tab === "clubes" && (
            <div>
              <label className="overline block mb-1">Plan</label>
              <select data-testid="filter-plan" value={fPlan} onChange={(e) => setFPlan(e.target.value)}
                className="h-10 bg-card border border-border px-2 font-mono text-xs focus:border-primary focus:outline-none">
                <option value="todos">Todos</option>
                <option value="presencia">Presencia</option>
                <option value="club">Club</option>
                <option value="total">Total</option>
              </select>
            </div>
          )}
          <div>
            <label className="overline block mb-1">Fecha de</label>
            <select data-testid="filter-date-field" value={fDateField} onChange={(e) => setFDateField(e.target.value)}
              className="h-10 bg-card border border-border px-2 font-mono text-xs focus:border-primary focus:outline-none">
              <option value="alta">Alta</option>
              <option value="vencimiento">Vencimiento</option>
            </select>
          </div>
          <div>
            <label className="overline block mb-1">Desde</label>
            <input data-testid="filter-date-from" type="date" value={fDesde} onChange={(e) => setFDesde(e.target.value)}
              className="h-10 bg-card border border-border px-2 font-mono text-xs focus:border-primary focus:outline-none" />
          </div>
          <div>
            <label className="overline block mb-1">Hasta</label>
            <input data-testid="filter-date-to" type="date" value={fHasta} onChange={(e) => setFHasta(e.target.value)}
              className="h-10 bg-card border border-border px-2 font-mono text-xs focus:border-primary focus:outline-none" />
          </div>
          <div>
            <label className="overline block mb-1">Ordenar</label>
            <select data-testid="filter-sort" value={fSort} onChange={(e) => setFSort(e.target.value)}
              className="h-10 bg-card border border-border px-2 font-mono text-xs focus:border-primary focus:outline-none">
              <option value="recientes">Más recientes</option>
              <option value="nombre">Nombre A-Z</option>
              <option value="vencimiento">Vencimiento próximo</option>
            </select>
          </div>
        </div>
        <div className="flex items-center gap-3 mt-3">
          <span data-testid="filter-count" className="text-xs font-mono text-muted-foreground">
            {visible.length} de {baseList.length} {tab === "player" ? "familias" : "clubes"}
          </span>
          {filtersActive && (
            <button data-testid="filter-clear-btn" onClick={clearFilters}
              className="text-[11px] font-heading uppercase tracking-wider text-amber-400 hover:text-amber-300">
              ✕ Limpiar filtros
            </button>
          )}
          <button data-testid="export-filtered-btn" onClick={exportFiltered} disabled={visible.length === 0}
            className="ml-auto h-8 px-3 border border-emerald-500/50 text-emerald-400 text-[10px] uppercase font-heading font-black tracking-widest hover:bg-emerald-500/10 disabled:opacity-40">
            ⬇ Exportar Excel ({visible.length})
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {loading && <div className="text-muted-foreground text-sm">Cargando…</div>}
        {!loading && visible.length === 0 && (
          <div className="col-span-full text-center py-12 border border-dashed border-border">
            <div className="overline">{filtersActive ? "Sin resultados con estos filtros" : tab === "player" ? "Ninguna familia suscrita todavía" : "Ningún club creado"}</div>
            <div className="mt-2 text-muted-foreground text-sm">{filtersActive ? "Prueba a limpiar los filtros o cambiar la búsqueda." : tab === "player" ? "Las familias se dan de alta solas desde la landing /player." : "Crea el primer club para empezar."}</div>
          </div>
        )}
        {visible.map((c) => {
          const isPlayer = c.plan === "familia";
          const plan = PLAN_INFO[c.plan] || PLAN_INFO.presencia;
          const status = PLAN_STATUS[c.plan_status] || PLAN_STATUS.trial;
          const subdomainUrl = c.subdomain || "—";
          return (
            <div key={c.id} data-testid={`club-card-${c.id}`} className="card-tactical p-5">
              <div className="flex items-start justify-between">
                <div className="w-10 h-10 border border-border flex items-center justify-center">
                  <Building2 size={18} className="text-primary" />
                </div>
                <div className="flex items-center gap-1">
                  <button data-testid={`resend-kit-${c.id}`} onClick={() => resendKit(c)} className="p-1.5 text-muted-foreground hover:text-primary" title="Reenviar Kit de bienvenida"><Mail size={14} /></button>
                  <button data-testid={`edit-club-${c.id}`} onClick={() => openEdit(c)} className="p-1.5 text-muted-foreground hover:text-primary" title="Editar plan"><Pencil size={14} /></button>
                  {user?.role === "super_admin" && (
                    <button data-testid={`delete-club-${c.id}`} onClick={() => remove(c.id)} className="p-1.5 text-muted-foreground hover:text-destructive" title="Eliminar"><Trash2 size={14} /></button>
                  )}
                </div>
              </div>
              <div className="mt-4">
                <div className="font-heading font-bold text-xl uppercase tracking-tight leading-tight">{c.name}</div>
                <div className="text-xs text-muted-foreground mt-1">{c.city || "—"}</div>
                <div className="mt-3 inline-flex items-center gap-1.5 text-xs font-mono text-primary" data-testid={`club-subdomain-${c.id}`}>
                  {subdomainUrl}
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-border grid grid-cols-2 gap-2">
                <div>
                  <div className="overline">Plan</div>
                  <div className={`font-heading font-bold text-sm uppercase mt-1 ${plan.color}`}>{plan.label}</div>
                </div>
                <div>
                  <div className="overline">Estado</div>
                  <div className={`font-heading font-bold text-sm uppercase mt-1 flex items-center gap-1 ${status.color}`}>
                    <CircleDot size={11} /> {status.label}
                  </div>
                  <div className="text-[10px] text-muted-foreground font-mono">{c.seats ? `${c.seats} ${isPlayer ? "hijo/s" : "usuarios"}` : "—"}</div>
                </div>
              </div>
              {user?.role === "super_admin" && (
                <button
                  onClick={(e) => { e.stopPropagation(); impersonate(c); }}
                  disabled={impersonatingId === c.id}
                  data-testid={`impersonate-club-${c.id}`}
                  className="mt-4 w-full h-9 inline-flex items-center justify-center gap-2 border-2 border-emerald-500/40 text-emerald-400 text-xs uppercase font-heading tracking-widest hover:bg-emerald-500/10 disabled:opacity-60"
                >
                  <LogIn size={12} />
                  {impersonatingId === c.id ? "Entrando…" : "Entrar como admin"}
                </button>
              )}
              {user?.role === "super_admin" || user?.role === "platform_sales" ? !isPlayer && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (window.confirm(`Se generará una contraseña NUEVA para el admin de "${c.name}" (la anterior dejará de funcionar). Si el club no tiene admin, se creará.\n\n¿Continuar?`)) adminAccess(c);
                  }}
                  data-testid={`admin-access-${c.id}`}
                  className="mt-2 w-full h-9 inline-flex items-center justify-center gap-2 border-2 border-amber-500/40 text-amber-400 text-xs uppercase font-heading tracking-widest hover:bg-amber-500/10"
                  title="Crea el usuario admin del club o le genera una contraseña nueva, y te la muestra para pasársela"
                >
                  🔑 Crear/Resetear acceso
                </button>
              ) : null}
              {!isPlayer && (
                <button
                  onClick={(e) => { e.stopPropagation(); markPaid(c); }}
                  data-testid={`mark-paid-${c.id}`}
                  className="mt-2 w-full h-9 inline-flex items-center justify-center gap-2 border-2 border-primary/40 text-primary text-xs uppercase font-heading tracking-widest hover:bg-primary/10"
                  title="Activar el plan como pagado por adelantado (sin cobro por Stripe)"
                >
                  Activar sin cobro
                </button>
              )}
              {!isPlayer && (
                <button
                  onClick={(e) => { e.stopPropagation(); paymentLink(c); }}
                  data-testid={`payment-link-${c.id}`}
                  className="mt-2 w-full h-9 inline-flex items-center justify-center gap-2 border border-border text-muted-foreground hover:text-primary hover:border-primary text-xs uppercase font-heading tracking-widest transition-colors"
                  title="Genera un enlace de pago Stripe para enviar por WhatsApp (sirve también para clubes vencidos)"
                >
                  <Link2 size={12} /> Enlace de pago
                </button>
              )}
            </div>
          );
        })}
      </div>
      </>)}

      {tab === "equipo" && user?.role === "super_admin" && (
        <>
          <PlatformTeam setAccessCreds={setAccessCreds} />
          <ClubUsersManager clubs={clubAccounts} setAccessCreds={setAccessCreds} />
        </>
      )}

      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-card border border-border w-full max-w-lg max-h-[92vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-border sticky top-0 bg-card z-10">
              <div>
                <div className="overline">{editing ? "Editar" : "Nuevo"}</div>
                <div className="font-heading font-black text-2xl uppercase tracking-tight">{editing ? editing.name : "Crear club"}</div>
              </div>
              <button onClick={() => setShowForm(false)} data-testid="close-form-btn" className="p-2 hover:text-primary">
                <X size={18} />
              </button>
            </div>
            <form onSubmit={submit} className="p-5 space-y-4">
              <Field label="Nombre del club *" value={form.name} onChange={setName} required tid="form-club-name" />
              <div>
                <label className="overline block mb-1">Subdominio *</label>
                <div className="flex items-stretch border border-border focus-within:border-primary">
                  <input
                    required
                    data-testid="form-club-subdomain"
                    value={form.subdomain}
                    onChange={(e) => setSubdomain(e.target.value)}
                    placeholder="cfdemo"
                    pattern="[a-z0-9-]+"
                    minLength={3}
                    maxLength={40}
                    className="flex-1 h-10 bg-transparent px-3 focus:outline-none font-mono text-sm"
                  />
                  <span className="px-3 flex items-center bg-secondary text-muted-foreground text-xs font-mono border-l border-border">identificador</span>
                </div>
                <div className="text-[10px] text-muted-foreground mt-1 font-mono">Solo letras minúsculas, números y guiones. Se genera automáticamente desde el nombre.</div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Ciudad" value={form.city} onChange={(v) => setForm({ ...form, city: v })} tid="form-club-city" />
                <Field label="Email de contacto" type="email" value={form.contact_email} onChange={(v) => setForm({ ...form, contact_email: v })} tid="form-club-email" />
                <Field label="Teléfono móvil de contacto" type="tel" value={form.contact_phone} onChange={(v) => setForm({ ...form, contact_phone: v })} tid="form-club-phone" placeholder="+34 600 000 000" />
              </div>

              <div className="pt-4 border-t border-border">
                <div className="overline mb-3">Suscripción</div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="overline block mb-1">Plan *</label>
                    <select data-testid="form-club-plan" value={form.plan} onChange={(e) => setForm({ ...form, plan: e.target.value })} className="w-full h-10 bg-card border border-border px-3 font-mono text-sm focus:border-primary focus:outline-none">
                      <option value="presencia">Plan Presencia</option>
                      <option value="club">Plan Club</option>
                      <option value="total">Plan Total</option>
                    </select>
                  </div>
                  <div>
                    <label className="overline block mb-1">Estado *</label>
                    <select data-testid="form-club-status" value={form.plan_status} onChange={(e) => setForm({ ...form, plan_status: e.target.value })} className="w-full h-10 bg-card border border-border px-3 font-mono text-sm focus:border-primary focus:outline-none">
                      <option value="trial">Prueba</option>
                      <option value="active">Activo</option>
                      <option value="suspended">Suspendido</option>
                      <option value="cancelled">Cancelado</option>
                    </select>
                  </div>
                  <Field label="Fin del plan (fecha)" type="date" value={form.plan_expires_at} onChange={(v) => setForm({ ...form, plan_expires_at: v })} tid="form-club-expires" hint="Fecha en la que expira la suscripción. Tras esta fecha sin renovar, el club recibe avisos y se suspende." />
                  <Field label="Nº máx. usuarios (asientos)" type="number" value={form.seats} onChange={(v) => setForm({ ...form, seats: v })} tid="form-club-seats" hint="Límite total de usuarios del club (admin + coordinadores + entrenadores) según el plan contratado. Dejar vacío = ilimitado." />
                </div>
              </div>

              {!editing && (
                <div className="pt-4 border-t border-border">
                  <div className="overline mb-3">Admin del club (opcional)</div>
                  <div className="space-y-3">
                    <Field label="Nombre" value={form.admin_name} onChange={(v) => setForm({ ...form, admin_name: v })} tid="form-admin-name" />
                    <Field label="Email" type="email" value={form.admin_email} onChange={(v) => setForm({ ...form, admin_email: v })} tid="form-admin-email" />
                    <Field label="Contraseña" type="password" value={form.admin_password} onChange={(v) => setForm({ ...form, admin_password: v })} tid="form-admin-pass" />
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setShowForm(false)} className="h-10 px-5 border border-border text-sm uppercase font-heading tracking-wider hover:border-primary">
                  Cancelar
                </button>
                <button type="submit" data-testid="submit-club-btn" className="btn-volt">
                  {editing ? "Guardar cambios" : "Crear club"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Clubes · Vencimientos */}
      <div className="mt-10 pt-8 border-t border-border" data-testid="club-expiry-panel">
        <div className="overline">Clubes B2B · Control comercial</div>
        <h2 className="font-heading font-black text-2xl uppercase tracking-tight mt-2">Vencimientos de clubes</h2>
        <p className="text-xs text-muted-foreground mt-2 max-w-xl">
          Nada se da de baja solo: cuando una prueba o plan vence, te llega un email y el club aparece aquí como
          <b className="text-red-400"> Vencido</b> hasta que tú decidas — llama, y luego amplía días o suspende.
        </p>
        <ClubExpirySection />
      </div>

      {/* Player · Pases de prueba */}
      <div className="mt-10 pt-8 border-t border-border" data-testid="player-trial-panel">
        <div className="overline">Player · Pases de prueba</div>
        <h2 className="font-heading font-black text-2xl uppercase tracking-tight mt-2">Crear cuenta de prueba Player</h2>
        <p className="text-xs text-muted-foreground mt-2 max-w-xl">
          Crea una cuenta de cortesía para que un padre pruebe Player unos días. Al vencer se bloquea sola y le llega
          un email con el enlace de pago — si paga con el mismo email, <b>recupera su cuenta con todos los datos</b>.
        </p>
        <PlayerTrialSection />
      </div>

      {/* Leads de clubes propuestos por familias */}
      <div className="mt-10 pt-8 border-t border-border" data-testid="club-leads-panel">
        <div className="overline">Player → Clubes · Leads B2B</div>
        <h2 className="font-heading font-black text-2xl uppercase tracking-tight mt-2">Clubes propuestos por familias</h2>
        <p className="text-xs text-muted-foreground mt-2 max-w-xl">
          Cada familia Player puede proponer el club real de su hijo/a. Llegan aquí (y a GHL como evento
          <code className="text-primary"> lead_club_familia</code>) para que los trabajes como leads B2B.
        </p>
        <ClubLeadsSection />
      </div>

      {/* Reporte semanal comercial */}
      <div className="mt-10 pt-8 border-t border-border" data-testid="weekly-report-panel">
        <div className="overline">Comercial · Reporte semanal</div>
        <h2 className="font-heading font-black text-2xl uppercase tracking-tight mt-2">Tu reporte del lunes, generado solo</h2>
        <p className="text-xs text-muted-foreground mt-2 max-w-xl">
          Resumen de los últimos 7 días con datos reales: pagos, altas, trials en marcha y suspendidos. Copia y envía.
        </p>
        <WeeklyReportSection />
      </div>

      {/* Actividad GHL */}
      <div className="mt-10 pt-8 border-t border-border" data-testid="ghl-activity-section">
        <div className="overline">Actividad · GoHighLevel</div>
        <h2 className="font-heading font-black text-2xl uppercase tracking-tight mt-2">Qué está disparando la app en GHL</h2>
        <p className="text-xs text-muted-foreground mt-2 max-w-xl">
          Registro de cada etiqueta, movimiento de oportunidad y evento que la app envía a GHL por API.
          Úsalo para comprobar que tus workflows reciben los triggers esperados.
        </p>
        <GhlActivitySection />
      </div>

      {/* Integración GHL */}
      {user?.role === "super_admin" && (
      <div className="mt-10 pt-8 border-t border-border" data-testid="ghl-settings-panel">
        <div className="overline">Integración · GoHighLevel</div>
        <h2 className="font-heading font-black text-2xl uppercase tracking-tight mt-2">Webhook de GHL (pagos → tag automático)</h2>
        <p className="text-xs text-muted-foreground mt-2 max-w-xl">
          Pega aquí la URL del <b>Inbound Webhook</b> de tu workflow en GHL. Cuando un club pague por Stripe, la app enviará
          el evento <code className="text-primary">pago_completado</code> (email, nombre, club y plan) para que GHL ponga el tag <b>cliente_pagante</b> solo.
        </p>
        <GhlWebhookSetting />
      </div>
      )}

      {/* Documentos internos */}
      <div className="mt-10 pt-8 border-t border-border">
        <div className="overline">Documentos internos · Super Admin</div>
        <h2 className="font-heading font-black text-2xl uppercase tracking-tight mt-2">Guías y credenciales</h2>
        <p className="text-xs text-muted-foreground mt-2 max-w-xl">
          Documentos descargables sólo visibles para el rol Super Admin. Guías comerciales para clubes + manuales internos de uso restringido.
        </p>

        <div className="overline mt-6 mb-2 text-primary">Guías para clientes (compartibles)</div>

        <div className="border border-primary/40 bg-primary/5 p-4 mb-4 flex flex-wrap items-center gap-3" data-testid="formacion-share-card">
          <div className="flex-1 min-w-[240px]">
            <div className="font-heading font-black text-sm uppercase tracking-tight">🎓 Formación Sudefootball · Documento maestro</div>
            <div className="text-[11px] text-muted-foreground mt-1">Funcionamiento completo con capturas + guion de vídeo por módulo. Tiene <b>URL pública</b>: compártela directamente con el cliente.</div>
            <div className="text-[11px] font-mono text-primary mt-1 break-all">{`${process.env.REACT_APP_BACKEND_URL}/api/public/docs/formacion`}</div>
          </div>
          <button
            data-testid="formacion-copy-link-btn"
            onClick={() => { navigator.clipboard.writeText(`${process.env.REACT_APP_BACKEND_URL}/api/public/docs/formacion`); toast.success("Enlace público copiado — pégalo en WhatsApp o email"); }}
            className="h-9 px-4 border border-primary text-primary text-[10px] uppercase font-heading font-black tracking-widest hover:bg-primary/10"
          >
            📋 Copiar enlace público
          </button>
        </div>

        <div className="border border-primary/40 bg-primary/5 p-4 mb-4 flex flex-wrap items-center gap-3" data-testid="video3min-share-card">
          <div className="flex-1 min-w-[240px]">
            <div className="font-heading font-black text-sm uppercase tracking-tight">🎬 Vídeo · Sudefootball en 2 minutos</div>
            <div className="text-[11px] text-muted-foreground mt-1">Vídeo vertical con voz en off: panel, plantilla, Pizarra en vivo, estadísticas y en directo. <b>URL pública</b> para WhatsApp/email.</div>
            <div className="text-[11px] font-mono text-primary mt-1 break-all">{`${process.env.REACT_APP_BACKEND_URL}/descargas/sudefootball-3min.mp4`}</div>
          </div>
          <div className="flex gap-2">
            <a
              data-testid="video3min-open-btn"
              href={`${process.env.REACT_APP_BACKEND_URL}/descargas/sudefootball-3min.mp4`}
              target="_blank" rel="noreferrer"
              className="h-9 px-4 border border-border text-foreground text-[10px] uppercase font-heading font-black tracking-widest hover:bg-white/5 inline-flex items-center"
            >▶ Ver</a>
            <button
              data-testid="video3min-copy-link-btn"
              onClick={() => { navigator.clipboard.writeText(`${process.env.REACT_APP_BACKEND_URL}/descargas/sudefootball-3min.mp4`); toast.success("Enlace del vídeo copiado"); }}
              className="h-9 px-4 border border-primary text-primary text-[10px] uppercase font-heading font-black tracking-widest hover:bg-primary/10"
            >📋 Copiar enlace</button>
          </div>
        </div>

        <div className="border border-primary/40 bg-primary/5 p-4 mb-4 flex flex-wrap items-center gap-3" data-testid="videopizarra-share-card">
          <div className="flex-1 min-w-[240px]">
            <div className="font-heading font-black text-sm uppercase tracking-tight">🎬 Vídeo · La Pizarra en vivo (estrella)</div>
            <div className="text-[11px] text-muted-foreground mt-1">Solo la Pizarra: setup, gol, tarjeta, cambios, datos, compartir alineación, en directo y cierre. <b>URL pública</b>.</div>
            <div className="text-[11px] font-mono text-primary mt-1 break-all">{`${process.env.REACT_APP_BACKEND_URL}/descargas/pizarra-en-vivo.mp4`}</div>
          </div>
          <div className="flex gap-2">
            <a
              data-testid="videopizarra-open-btn"
              href={`${process.env.REACT_APP_BACKEND_URL}/descargas/pizarra-en-vivo.mp4`}
              target="_blank" rel="noreferrer"
              className="h-9 px-4 border border-border text-foreground text-[10px] uppercase font-heading font-black tracking-widest hover:bg-white/5 inline-flex items-center"
            >▶ Ver</a>
            <button
              data-testid="videopizarra-copy-link-btn"
              onClick={() => { navigator.clipboard.writeText(`${process.env.REACT_APP_BACKEND_URL}/descargas/pizarra-en-vivo.mp4`); toast.success("Enlace del vídeo de la Pizarra copiado"); }}
              className="h-9 px-4 border border-primary text-primary text-[10px] uppercase font-heading font-black tracking-widest hover:bg-primary/10"
            >📋 Copiar enlace</button>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-3">
          {[
            { key: "formacion-clientes-pdf", label: "🎓 Formación Sudefootball · Doc maestro + guiones de vídeo", hint: "Todo el funcionamiento con capturas y guion de grabación por módulo — base de los vídeos a clientes", ext: "pdf" },
            { key: "club-guide-pdf", label: "Guía completa · Clubes", hint: "Reenviar por email a clubes potenciales", ext: "pdf" },
            { key: "coordinator-guide-pdf", label: "Guía · Coordinadores y Directores Deportivos", hint: "Para el coordinador del club", ext: "pdf" },
            { key: "coach-guide-pdf", label: "Guía · Entrenadores y Delegados", hint: "Uso diario del entrenador/delegado", ext: "pdf" },
            ...(user?.role === "super_admin" ? [
              { key: "video-script-pdf", label: "Guion de vídeo · Onboarding", hint: "Script listo para grabar tu tutorial de bienvenida", ext: "pdf" },
              { key: "video-script-leads-pdf", label: "Guion de vídeo · Captación leads (3 min)", hint: "Script palabra por palabra para el Email 1 de GHL (Meta Ads)", ext: "pdf" },
              { key: "chatbot-kb-pdf", label: "Base de conocimiento · Chatbot GHL", hint: "Documento v2 para entrenar el bot: producto, precios con promo, FAQs", ext: "pdf" },
            ] : []),
          ].map(d => <DocDownloadCard key={d.key} doc={d} />)}
        </div>

        {user?.role === "super_admin" && (<>
        <div className="overline mt-8 mb-2 text-primary">Kit visual Meta Ads · Imágenes generadas ★ NUEVO</div>
        <p className="text-xs text-muted-foreground mb-3">
          Imágenes 1:1 (1080×1080) listas para subir a Meta Ads Manager, Instagram y Facebook.
          Descárgalas, súbelas a la plataforma y crea un adset con cada una.
        </p>
        <div className="grid md:grid-cols-4 gap-3">
          {[
            { key: "hero-dashboard", label: "01 · Hero Dashboard", hint: "Todo tu club en una pantalla" },
            { key: "antes-despues", label: "02 · Antes / Después", hint: "Libreta rota vs móvil elegante" },
            { key: "testimonio-coordinador", label: "03 · Testimonio Coordinador", hint: "Cita de un coach real" },
            { key: "stat-87", label: "04 · Stat 87%", hint: "Minimal billboard estilo OOH" },
          ].map(img => (
            <button
              key={img.key}
              data-testid={`download-image-${img.key}`}
              onClick={async () => {
                try {
                  const token = localStorage.getItem("access_token");
                  const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/marketing/image/${img.key}`, {
                    headers: { Authorization: `Bearer ${token}` },
                  });
                  if (!resp.ok) { toast.error("No se pudo descargar"); return; }
                  const blob = await resp.blob();
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url; a.download = `${img.key}.png`;
                  document.body.appendChild(a); a.click(); a.remove();
                  URL.revokeObjectURL(url);
                  toast.success(`${img.label} descargada`);
                } catch { toast.error("Error de red"); }
              }}
              className="border border-border p-4 text-left hover:border-primary hover:bg-primary/5 transition-colors group">
              <div className="font-heading font-black text-sm uppercase tracking-tight group-hover:text-primary">{img.label}</div>
              <div className="text-[10px] text-muted-foreground mt-1">{img.hint}</div>
              <div className="text-[10px] text-primary mt-2 opacity-0 group-hover:opacity-100 transition-opacity">Descargar PNG →</div>
            </button>
          ))}
        </div>
        </>)}

        <div className="overline mt-8 mb-2 text-amber-400">Uso interno Sudeporte · Confidencial</div>
        <div className="grid md:grid-cols-3 gap-3">
          {[
            { key: "closer-sales-pdf", label: "Guía comercial · Closer de Ventas", hint: "Proceso completo: lead → cualificación → trial 15 días → cierre", ext: "pdf" },
            { key: "demo-credentials-pdf", label: "Claves de acceso · Cuentas Demo", hint: "Accesos al CF Demo (admin, coordinador, entrenador) y familia Player para hacer demos en vivo", ext: "pdf" },
            { key: "sales-playbook-pdf", label: "Playbook Comercial · Protocolo del equipo", hint: "Del lead al cliente en 365 días — scripts + objeciones + onboarding", ext: "pdf" },
            { key: "club-emails-pdf", label: "Comunicaciones automáticas · Clubes", hint: "Qué envía la app sola: kit de bienvenida, avisos de trial, suspensión y pago", ext: "pdf" },
            { key: "resumen-operativo-pdf", label: "Resumen operativo · Clubes + Player", hint: "El guion maestro: qué hace la app sola (con textos) y qué toques haces tú a mano", ext: "pdf" },
            ...(user?.role === "super_admin" ? [
              { key: "internal-team-pdf", label: "Manual interno · Equipo Sudeporte", hint: "⚠️ Acceso total. No compartir con clientes.", ext: "pdf" },
              { key: "marketing-meta-pdf", label: "Kit Meta Ads · Vídeos + Imágenes + Copies", hint: "Campaña Meta lista para lanzar (4 vídeos + 8 imágenes + posts)", ext: "pdf" },
              { key: "meta-ads-player-pdf", label: "Guía Meta Ads · Player (B2C)", hint: "Paso a paso para lanzar tu campaña a la landing /player: píxel, presupuesto, métricas", ext: "pdf" },
              { key: "video-ad-player", label: "Vídeo anuncio · Player (Reels 9:16)", hint: "Vídeo 26s 1080x1920 listo para subir a Meta Ads — añade música en el editor de Meta", ext: "mp4" },
              { key: "video-bienvenida-player", label: "Vídeo bienvenida · Player (onboarding 9:16)", hint: "Vídeo 16s con los 3 primeros pasos — para GHL, WhatsApp o redes", ext: "mp4" },
              { key: "landing-ghl-html", label: "Landing B2B para GHL (v2)", hint: "HTML completo rediseñado — descarga y pega el código en tu funnel de GoHighLevel", ext: "html" },
              { key: "ghl-automation-pdf", label: "Workflows GHL · Clubes (B2B)", hint: "⚠️ Solo super_admin. Workflows completos a montar en GoHighLevel para clubes.", ext: "pdf" },
              { key: "player-flujo-pdf", label: "Player · Flujo completo (App + GHL)", hint: "⚠️ Solo super_admin. Qué manda la app sola (textos exactos) + 6 workflows GHL a montar con mensajes listos", ext: "pdf" },
              { key: "ghl-player-pdf", label: "GHL Player · Conexión inicial (legado)", hint: "⚠️ Solo super_admin. Configuración del Inbound Webhook + workflow carrito.", ext: "pdf" },
              { key: "test-credentials", label: "Credenciales internas", hint: "⚠️ Confidencial — no compartir", ext: "md" },
              { key: "prd", label: "PRD del producto", hint: "Especificación técnica", ext: "md" },
            ] : []),
          ].map(d => <DocDownloadCard key={d.key} doc={d} />)}
        </div>
      </div>
    </div>
  );
}

function DocDownloadCard({ doc }) {
  return (
    <button
      data-testid={`download-doc-${doc.key}`}
      onClick={async () => {
        try {
          const token = localStorage.getItem("access_token");
          const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/docs/${doc.key}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (!resp.ok) { toast.error("No se pudo descargar"); return; }
          // Use filename from Content-Disposition if available, else fallback to key + ext.
          const cd = resp.headers.get("content-disposition") || "";
          const m = /filename="?([^";]+)"?/i.exec(cd);
          const fname = m ? m[1] : `${doc.key}.${doc.ext}`;
          const blob = await resp.blob();
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url; a.download = fname;
          document.body.appendChild(a); a.click(); a.remove();
          URL.revokeObjectURL(url);
          toast.success(`${doc.label} descargado`);
        } catch (e) { toast.error("Error de red"); }
      }}
      className="border border-border p-4 text-left hover:border-primary hover:bg-primary/5 transition-colors group"
    >
      <div className="flex items-center gap-2 text-primary">
        <ExternalLink size={14} />
        <span className="overline text-[10px]">Descargar .{doc.ext}</span>
      </div>
      <div className="font-heading font-bold uppercase tracking-tight text-sm mt-2 group-hover:text-primary transition-colors">{doc.label}</div>
      <div className="text-[11px] text-muted-foreground mt-1">{doc.hint}</div>
    </button>
  );
}

function Field({ label, value, onChange, type = "text", required, tid, placeholder, hint }) {
  return (
    <div>
      <label className="overline block mb-1">{label}</label>
      {type === "date" ? (
        <SharedDateField value={value || ""} onChange={onChange} testid={tid} placeholder={placeholder || "Selecciona fecha"} />
      ) : (
        <input
          type={type}
          required={required}
          placeholder={placeholder}
          data-testid={tid}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className="w-full h-10 bg-transparent border border-border px-3 focus:border-primary focus:outline-none transition-colors duration-200 font-mono text-sm"
        />
      )}
      {hint && <div className="text-[10px] text-muted-foreground mt-1 leading-relaxed">{hint}</div>}
    </div>
  );
}

function SummaryStat({ label, value }) {
  return (
    <div className="p-5">
      <div className="overline">{label}</div>
      <div className="metric-number text-4xl mt-2">{value}</div>
    </div>
  );
}

function ClubLeadsSection() {
  const [leads, setLeads] = useState([]);
  const load = () => api.get("/admin/club-leads").then(({ data }) => setLeads(data.leads || [])).catch(() => {});
  useEffect(() => { load(); }, []);
  const setStatus = async (id, status) => {
    try {
      const { data } = await api.put(`/admin/club-leads/${id}`, { status });
      if (data.reward === "granted") toast.success("Estado actualizado — 🎁 1 mes de regalo aplicado a la familia (crédito Stripe + email enviado)");
      else if (data.reward === "no_stripe") toast.warning("Estado actualizado, pero la familia no tiene cliente Stripe (¿cuenta de prueba?) — regalo NO aplicado");
      else if (data.reward === "stripe_error") toast.error("Estado actualizado, pero el crédito Stripe falló — revisa logs");
      else toast.success("Estado actualizado");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  if (leads.length === 0) return <div className="mt-4 text-xs text-muted-foreground" data-testid="club-leads-empty">Aún no hay clubes propuestos.</div>;
  return (
    <div className="mt-4 border border-border divide-y divide-border max-w-3xl" data-testid="club-leads-list">
      {leads.map((l) => (
        <div key={l.id} className="px-3 py-2 flex items-center justify-between gap-3 flex-wrap text-xs">
          <div className="min-w-0">
            <b>{l.club_name}</b>{l.city ? ` · ${l.city}` : ""}
            {l.contact_info && <span className="text-muted-foreground"> · contacto: {l.contact_info}</span>}
            <div className="text-[10px] text-muted-foreground">
              Propuesto por {l.proposed_by_name} · {l.proposed_by_email}{l.proposed_by_phone ? ` · ${l.proposed_by_phone}` : ""} · {(l.created_at || "").slice(0, 10)}
            </div>
          </div>
          <select data-testid={`club-lead-status-${l.id}`} value={l.status} onChange={(e) => setStatus(l.id, e.target.value)}
                  className="h-8 bg-background border border-border px-2 text-xs focus:border-primary focus:outline-none shrink-0">
            <option value="nuevo">🆕 Nuevo</option>
            <option value="contactado">📞 Contactado</option>
            <option value="cliente">💰 Cliente</option>
            <option value="descartado">✖️ Descartado</option>
          </select>
        </div>
      ))}
    </div>
  );
}

function ClubExpirySection() {
  const [clubs, setClubs] = useState([]);
  const [filter, setFilter] = useState("todos");
  const load = () => api.get("/admin/clubs-overview").then(({ data }) => setClubs(data.clubs || [])).catch(() => {});
  useEffect(() => { load(); }, []);
  const isTrial = (c) => c.plan_status === "trial";
  const isVencido = (c) => c.days_left !== null && c.days_left < 0 && c.plan_status !== "suspended";
  const counters = {
    trial: clubs.filter(isTrial).length,
    trialSoon: clubs.filter((c) => isTrial(c) && c.days_left !== null && c.days_left >= 0 && c.days_left <= 7).length,
    active: clubs.filter((c) => c.plan_status === "active").length,
    vencidos: clubs.filter(isVencido).length,
    suspendidos: clubs.filter((c) => c.plan_status === "suspended").length,
  };
  const filtered = clubs.filter((c) => {
    if (filter === "prueba") return isTrial(c) && !isVencido(c);
    if (filter === "suscritos") return c.plan_status === "active" && !isVencido(c);
    if (filter === "vencidos") return isVencido(c);
    if (filter === "suspendidos") return c.plan_status === "suspended";
    return true;
  });
  const extend = async (c) => {
    const days = window.prompt(`¿Cuántos días más para ${c.name}? (desde hoy)`, "15");
    if (!days) return;
    const d = new Date(); d.setDate(d.getDate() + Number(days));
    try {
      await api.put(`/clubs/${c.id}`, { plan_expires_at: d.toISOString().slice(0, 10), plan_status: c.plan_status === "suspended" ? "trial" : c.plan_status });
      toast.success(`${c.name}: ampliado hasta ${d.toISOString().slice(0, 10)}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const suspend = async (c) => {
    if (!window.confirm(`¿Suspender el acceso de ${c.name}? (reversible con "Ampliar")`)) return;
    try {
      await api.put(`/clubs/${c.id}`, { plan_status: "suspended" });
      toast.success(`${c.name} suspendido`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const badge = (c) => {
    if (c.plan_status === "suspended") return <span className="text-muted-foreground">⛔ Suspendido</span>;
    if (isVencido(c)) return <span className="text-red-400 font-bold">🔴 Vencido hace {Math.abs(c.days_left)} d — decide</span>;
    if (c.days_left === null) return <span className="text-muted-foreground">Sin vencimiento</span>;
    const color = c.days_left <= 3 ? "text-red-400" : c.days_left <= 7 ? "text-amber-400" : "text-emerald-500";
    return <span className={color}>{isTrial(c) ? "🧪 Prueba" : "💳 Renueva"} · {c.plan_expires_at?.slice(0, 10)} (en {c.days_left} d)</span>;
  };
  const FBtn = ({ id, label, count }) => (
    <button data-testid={`club-filter-${id}`} onClick={() => setFilter(id)}
            className={`h-8 px-3 text-[11px] uppercase font-heading font-black tracking-wider border transition-colors ${filter === id ? "border-primary text-primary bg-primary/10" : "border-border text-muted-foreground hover:border-primary/50"}`}>
      {label} ({count})
    </button>
  );
  return (
    <div className="mt-4">
      <div className="flex flex-wrap gap-2" data-testid="club-expiry-counters">
        <FBtn id="todos" label="Todos" count={clubs.length} />
        <FBtn id="prueba" label={`En prueba${counters.trialSoon ? ` · ${counters.trialSoon} vencen ≤7d` : ""}`} count={counters.trial} />
        <FBtn id="suscritos" label="Suscritos" count={counters.active} />
        <FBtn id="vencidos" label="🔴 Vencidos" count={counters.vencidos} />
        <FBtn id="suspendidos" label="Suspendidos" count={counters.suspendidos} />
        <button data-testid="club-expiry-export" onClick={async () => {
          try {
            const resp = await api.get("/admin/clubs-overview/export.xlsx", { responseType: "blob" });
            const url = URL.createObjectURL(resp.data);
            const a = document.createElement("a");
            a.href = url; a.download = `clubes_vencimientos_${new Date().toISOString().slice(0, 10)}.xlsx`;
            a.click(); URL.revokeObjectURL(url);
          } catch { toast.error("No se pudo exportar"); }
        }} className="h-8 px-3 text-[11px] uppercase font-heading font-black tracking-wider border border-emerald-700 text-emerald-500 hover:bg-emerald-950/40 transition-colors">
          ⬇ Exportar Excel
        </button>
      </div>
      <div className="mt-3 border border-border divide-y divide-border max-w-4xl" data-testid="club-expiry-list">
        {filtered.length === 0 && <div className="px-3 py-3 text-xs text-muted-foreground">Sin clubes en este filtro.</div>}
        {filtered.map((c) => (
          <div key={c.id} className="px-3 py-2 flex items-center justify-between gap-3 flex-wrap text-xs">
            <div className="min-w-0">
              <b>{c.name}</b> <span className="text-muted-foreground">· plan {c.plan}</span>
              <div className="text-[10px] text-muted-foreground">{c.contact_email || "—"}{c.contact_phone ? ` · 📞 ${c.contact_phone}` : ""}</div>
            </div>
            <div className="flex items-center gap-2 shrink-0 font-mono text-[11px]">
              {badge(c)}
              <button data-testid={`club-extend-${c.id}`} onClick={() => extend(c)}
                      className="h-7 px-2 border border-primary/60 text-primary hover:bg-primary/10 text-[10px] uppercase font-heading tracking-wider">Ampliar</button>
              {c.plan_status !== "suspended" && (
                <button data-testid={`club-suspend-${c.id}`} onClick={() => suspend(c)}
                        className="h-7 px-2 border border-red-800 text-red-400 hover:bg-red-950/40 text-[10px] uppercase font-heading tracking-wider">Suspender</button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function PlayerTrialSection() {
  const [form, setForm] = useState({ email: "", name: "", phone: "", days: 7 });
  const [creating, setCreating] = useState(false);
  const [result, setResult] = useState(null);
  const [trials, setTrials] = useState([]);
  const load = () => api.get("/admin/player-trials").then(({ data }) => setTrials(data.trials || [])).catch(() => {});
  useEffect(() => { load(); }, []);
  const create = async (e) => {
    e.preventDefault();
    setCreating(true); setResult(null);
    try {
      const { data } = await api.post("/admin/player-trial", form);
      setResult(data);
      setForm({ email: "", name: "", phone: "", days: 7 });
      toast.success("Cuenta de prueba creada — invitación enviada por email");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setCreating(false); }
  };
  const extend = async (t) => {
    const days = window.prompt(`¿Cuántos días de prueba desde HOY para ${t.name}?`, "7");
    if (!days) return;
    try {
      const { data } = await api.post(`/admin/player-trial/${t.id}/extend`, { days: Number(days) });
      toast.success(`Prueba ampliada hasta ${data.ends_at} — email enviado a la familia`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const statusLabel = (t) => {
    if (t.plan_status === "suspended") return <span className="text-red-500">Vencido</span>;
    if (t.plan_status === "active") return <span className="text-emerald-500">Convertido 💰</span>;
    return <span className="text-amber-400">Activo hasta {t.player_trial_ends_at}</span>;
  };
  return (
    <div className="mt-4 max-w-2xl">
      <form onSubmit={create} className="flex flex-wrap items-center gap-2">
        <input data-testid="player-trial-name" required type="text" placeholder="Nombre del padre/madre"
               value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
               className="h-10 flex-1 min-w-[150px] bg-transparent border border-border px-3 text-xs focus:border-primary focus:outline-none" />
        <input data-testid="player-trial-email" required type="email" placeholder="Email"
               value={form.email} onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))}
               className="h-10 flex-1 min-w-[170px] bg-transparent border border-border px-3 text-xs focus:border-primary focus:outline-none" />
        <input data-testid="player-trial-phone" required type="tel" placeholder="Móvil *"
               value={form.phone} onChange={(e) => setForm(f => ({ ...f, phone: e.target.value }))}
               className="h-10 flex-1 min-w-[130px] bg-transparent border border-border px-3 text-xs focus:border-primary focus:outline-none" />
        <select data-testid="player-trial-days" value={form.days}
                onChange={(e) => setForm(f => ({ ...f, days: Number(e.target.value) }))}
                className="h-10 bg-background border border-border px-2 text-xs focus:border-primary focus:outline-none">
          <option value={3}>3 días</option>
          <option value={7}>7 días</option>
          <option value={14}>14 días</option>
        </select>
        <button data-testid="player-trial-create" type="submit" disabled={creating}
                className="h-10 px-4 bg-primary text-white text-xs uppercase font-heading tracking-wider hover:bg-primary/90 disabled:opacity-50">
          {creating ? "Creando…" : "Crear pase"}
        </button>
      </form>
      {result && (
        <div data-testid="player-trial-result" className="mt-3 border border-emerald-700/50 bg-emerald-950/30 p-3 text-xs font-mono">
          ✅ Enviado a <b>{result.email}</b> · Contraseña: <b>{result.password}</b> · Vence: {result.ends_at}
        </div>
      )}
      {trials.length > 0 && (
        <div className="mt-4 border border-border divide-y divide-border" data-testid="player-trial-list">
          {trials.map(t => (
            <div key={t.id} className="flex items-center justify-between gap-2 px-3 py-2 text-xs flex-wrap">
              <div className="truncate">
                <b>{t.name}</b> · {t.contact_email}{t.contact_phone ? ` · ${t.contact_phone}` : ""}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {statusLabel(t)}
                {t.plan_status !== "active" && (
                  <button data-testid={`player-trial-extend-${t.id}`} onClick={() => extend(t)}
                          className="h-7 px-2 border border-primary/60 text-primary hover:bg-primary/10 text-[10px] uppercase font-heading tracking-wider transition-colors">
                    {t.plan_status === "suspended" ? "Rescatar" : "Ampliar"}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      <p className="mt-2 text-[10px] text-muted-foreground">
        💡 Nunca borres un pase (bloquearía el email). Para pasarlo a pago: que pague en la landing con su mismo email — su cuenta se activa conservando los datos.
      </p>
    </div>
  );
}

function WeeklyReportSection() {
  const [rep, setRep] = useState(null);
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    api.get("/admin/weekly-sales-report").then(({ data }) => setRep(data)).catch(() => setRep({ error: true }));
  }, []);
  if (!rep) return <div className="text-xs text-muted-foreground mt-3">Generando reporte…</div>;
  if (rep.error) return <div className="text-xs text-destructive mt-3">No se pudo generar el reporte</div>;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(rep.text);
      setCopied(true);
      toast.success("Reporte copiado — pégalo en WhatsApp o email");
      setTimeout(() => setCopied(false), 3000);
    } catch {
      window.prompt("Copia el reporte:", rep.text);
    }
  };
  return (
    <div className="mt-4" data-testid="weekly-report-section">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        {[["Pagos cobrados", `${rep.pagos} · ${(rep.total_cobrado || 0).toFixed(2)} €`],
          ["Altas clubes", rep.altas_clubes],
          ["Altas Player", rep.altas_player],
          ["Trials activos", (rep.trials || []).length]].map(([k, v]) => (
          <div key={k} className="border border-border p-4">
            <div className="overline">{k}</div>
            <div className="font-heading font-black text-2xl mt-1">{v}</div>
          </div>
        ))}
      </div>
      <pre data-testid="weekly-report-text" className="border border-border bg-secondary/30 p-4 text-xs whitespace-pre-wrap font-sans leading-relaxed max-h-72 overflow-y-auto">{rep.text}</pre>
      <button onClick={copy} data-testid="weekly-report-copy-btn"
        className="mt-3 h-10 px-5 inline-flex items-center gap-2 bg-primary text-primary-foreground text-xs uppercase font-heading font-black tracking-widest hover:opacity-90">
        {copied ? "✓ Copiado" : "Copiar reporte"}
      </button>
      <p className="text-[11px] text-muted-foreground mt-2">Se genera solo con los datos de los últimos 7 días. Añade a mano tus negociaciones abiertas antes de enviarlo. David lo recibe también por email cada lunes a las 8:00.</p>
    </div>
  );
}

function GhlActivitySection() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  useEffect(() => {
    api.get(`/ghl/activity?days=${days}`).then(({ data }) => setData(data)).catch(() => setData({ error: true }));
  }, [days]);
  if (!data) return <div className="text-xs text-muted-foreground mt-3">Cargando actividad…</div>;
  if (data.error) return <div className="text-xs text-destructive mt-3">No se pudo cargar la actividad GHL</div>;
  const Group = ({ title, items, testid }) => (
    <div className="border border-border p-4" data-testid={testid}>
      <div className="overline mb-2">{title}</div>
      {Object.keys(items || {}).length === 0 ? (
        <div className="text-xs text-muted-foreground">Sin actividad en este periodo</div>
      ) : (
        <div className="flex flex-wrap gap-2">
          {Object.entries(items).map(([k, v]) => (
            <span key={k} className="inline-flex items-center gap-1.5 border border-border bg-secondary/40 px-2 py-1 text-[11px]">
              <code className="text-primary">{k}</code>
              <b className="font-heading">{v}</b>
            </span>
          ))}
        </div>
      )}
    </div>
  );
  return (
    <div className="mt-4 space-y-3" data-testid="ghl-activity-panel">
      <div className="flex items-center gap-2">
        {[7, 30, 90].map((d) => (
          <button key={d} data-testid={`ghl-activity-days-${d}`} onClick={() => setDays(d)}
            className={`h-8 px-3 text-[11px] uppercase font-heading tracking-wider border transition-colors ${days === d ? "border-primary text-primary bg-primary/10" : "border-border text-muted-foreground hover:border-primary"}`}>
            {d} días
          </button>
        ))}
        <span className="text-xs text-muted-foreground ml-2" data-testid="ghl-activity-total">{data.total} disparos registrados</span>
      </div>
      <div className="grid md:grid-cols-3 gap-3">
        <Group title="Etiquetas puestas" items={data.tags} testid="ghl-activity-tags" />
        <Group title="Oportunidades movidas" items={data.stages} testid="ghl-activity-stages" />
        <Group title="Eventos al webhook" items={data.events} testid="ghl-activity-events" />
      </div>
      {(data.recent || []).length > 0 && (
        <div className="border border-border overflow-x-auto">
          <table className="w-full text-xs">
            <thead><tr className="border-b border-border text-muted-foreground text-left">
              <th className="p-2">Cuándo</th><th className="p-2">Tipo</th><th className="p-2">Contacto</th><th className="p-2">Detalle</th>
            </tr></thead>
            <tbody>
              {data.recent.slice(0, 15).map((r) => (
                <tr key={r.id} className="border-b border-border/50">
                  <td className="p-2 whitespace-nowrap text-muted-foreground">{(r.at || "").slice(0, 16).replace("T", " ")}</td>
                  <td className="p-2 uppercase font-heading">{r.kind}</td>
                  <td className="p-2">{r.email || "—"}</td>
                  <td className="p-2">
                    {(r.tags_added || []).map((t) => <code key={t} className="text-emerald-400 mr-1">+{t}</code>)}
                    {(r.tags_removed || []).map((t) => <code key={t} className="text-destructive mr-1">-{t}</code>)}
                    {r.stage && <code className="text-primary">{r.pipeline} → {r.stage}</code>}
                    {r.event && <code className="text-sky-400">{r.event}</code>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function GhlWebhookSetting() {
  const [url, setUrl] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/ghl/settings/webhook-url").then(({ data }) => setUrl(data.url || "")).catch(() => {});
  }, []);
  const save = async () => {
    setSaving(true);
    try {
      await api.put("/ghl/settings/webhook-url", { url });
      toast.success(url ? "URL de GHL guardada — los pagos ya avisarán a GHL" : "URL de GHL vaciada (avisos desactivados)");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <div className="flex items-center gap-2 mt-4 max-w-2xl">
      <input
        data-testid="ghl-webhook-url-input"
        type="text"
        value={url}
        onChange={(e) => setUrl(e.target.value)}
        placeholder="https://services.leadconnectorhq.com/hooks/..."
        className="flex-1 h-10 bg-transparent border border-border px-3 font-mono text-xs focus:border-primary focus:outline-none"
      />
      <button data-testid="ghl-webhook-url-save" onClick={save} disabled={saving}
              className="h-10 px-4 bg-primary text-white text-xs uppercase font-heading tracking-wider hover:bg-primary/90 disabled:opacity-50">
        {saving ? "Guardando…" : "Guardar"}
      </button>
    </div>
  );
}

const ROLE_LABEL = {
  super_admin: { label: "Super Admin", color: "text-red-400 border-red-400/50 bg-red-400/10" },
  platform_sales: { label: "Ventas", color: "text-sky-400 border-sky-400/50 bg-sky-400/10" },
};

function PlatformTeam({ setAccessCreds }) {
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ email: "", name: "", role: "platform_sales" });
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/platform/users").then(({ data }) => setUsers(data.users || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const provision = async (payload) => {
    const roleTxt = payload.role === "super_admin" ? "SUPER ADMIN (control total de la plataforma)" : "Ventas (como Henry)";
    if (!window.confirm(`Se generará una contraseña NUEVA para ${payload.email} con rol ${roleTxt}.\nSi ya existe, la anterior dejará de funcionar.\n\n¿Continuar?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/platform/users", payload);
      setAccessCreds({ ...data, clubName: "Plataforma Sudefootball" });
      setForm({ email: "", name: "", role: "platform_sales" });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="mt-10 border-2 border-red-400/30 bg-red-400/[0.02]" data-testid="platform-team-card">
      <div className="p-4 border-b border-red-400/20 flex items-center gap-2">
        <Users size={16} className="text-red-400" />
        <span className="overline text-red-400">👥 Equipo plataforma · Solo Super Admin</span>
      </div>
      <div className="divide-y divide-border">
        {users.map((u) => {
          const r = ROLE_LABEL[u.role] || ROLE_LABEL.platform_sales;
          return (
            <div key={u.id} className="flex items-center gap-3 p-3 flex-wrap" data-testid={`platform-user-${u.id}`}>
              <div className="flex-1 min-w-[200px]">
                <div className="font-heading font-bold uppercase tracking-tight text-sm">{u.name || u.email}</div>
                <div className="text-xs text-muted-foreground font-mono">{u.email}</div>
              </div>
              <span className={`border px-2 py-1 text-[10px] font-heading font-black uppercase tracking-wider ${r.color}`}>{r.label}</span>
              <button
                data-testid={`platform-user-reset-${u.id}`}
                onClick={() => provision({ email: u.email, name: u.name, role: u.role })}
                disabled={busy}
                className="h-8 px-3 border border-amber-500/40 text-amber-400 text-[10px] uppercase font-heading tracking-widest hover:bg-amber-500/10 disabled:opacity-50"
              >
                🔑 Resetear clave
              </button>
            </div>
          );
        })}
      </div>
      <form
        onSubmit={(e) => { e.preventDefault(); provision(form); }}
        className="p-4 border-t border-red-400/20 flex flex-wrap items-end gap-3"
      >
        <div className="flex-1 min-w-[200px]">
          <label className="overline block mb-1">Email *</label>
          <input data-testid="platform-user-email" required type="email" value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            placeholder="info@bergarsport.com"
            className="w-full h-10 bg-card border border-border px-3 font-mono text-sm focus:border-primary focus:outline-none" />
        </div>
        <div className="min-w-[140px]">
          <label className="overline block mb-1">Nombre</label>
          <input data-testid="platform-user-name" value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Lola"
            className="w-full h-10 bg-card border border-border px-3 font-mono text-sm focus:border-primary focus:outline-none" />
        </div>
        <div>
          <label className="overline block mb-1">Rol *</label>
          <select data-testid="platform-user-role" value={form.role}
            onChange={(e) => setForm({ ...form, role: e.target.value })}
            className="h-10 bg-card border border-border px-2 font-mono text-xs focus:border-primary focus:outline-none">
            <option value="platform_sales">Ventas (como Henry)</option>
            <option value="super_admin">Super Admin (control total)</option>
          </select>
        </div>
        <button data-testid="platform-user-create-btn" disabled={busy}
          className="h-10 px-5 bg-red-400 text-black font-heading font-black text-xs uppercase tracking-widest hover:opacity-90 disabled:opacity-50">
          {busy ? "Creando…" : "➕ Crear / Resetear acceso"}
        </button>
      </form>
      <p className="px-4 pb-4 text-[11px] text-muted-foreground">
        La contraseña se muestra una sola vez y se envía por email a la persona. Un Super Admin tiene control total (crear, borrar, facturación). Ventas puede gestionar clubes pero no eliminarlos.
      </p>
    </div>
  );
}

const CLUB_ROLE_LABEL = {
  club_admin: { label: "Admin club", color: "text-primary border-primary/50 bg-primary/10" },
  coordinator: { label: "Coordinador", color: "text-emerald-400 border-emerald-400/50 bg-emerald-400/10" },
  director_deportivo: { label: "Dir. deportivo", color: "text-violet-400 border-violet-400/50 bg-violet-400/10" },
  coach: { label: "Entrenador", color: "text-amber-400 border-amber-400/50 bg-amber-400/10" },
  delegado: { label: "Delegado", color: "text-sky-400 border-sky-400/50 bg-sky-400/10" },
  familia: { label: "Familia", color: "text-pink-400 border-pink-400/50 bg-pink-400/10" },
};
const CARGO_SUGERIDOS = ["Presidente", "Vicepresidente", "Tesorero", "Secretario", "Vocal", "Coordinador deportivo", "Delegado de campo"];

function ClubUsersManager({ clubs, setAccessCreds }) {
  const [clubId, setClubId] = useState("");
  const [users, setUsers] = useState([]);
  const [loadingU, setLoadingU] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", cargo: "", role: "coordinator" });
  const [busy, setBusy] = useState(false);
  const clubName = clubs.find((c) => c.id === clubId)?.name || "";

  const loadUsers = async (cid) => {
    if (!cid) { setUsers([]); return; }
    setLoadingU(true);
    try {
      const { data } = await api.get(`/users?club_id=${cid}`);
      setUsers(data || []);
    } catch (e) { toast.error("No se pudieron cargar los usuarios"); }
    finally { setLoadingU(false); }
  };

  const provision = async (payload) => {
    if (!window.confirm(`Se generará una contraseña NUEVA para ${payload.email} en ${clubName}.\nSi el usuario ya existe, su clave anterior dejará de funcionar.\n\n¿Continuar?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/platform/club-users", { ...payload, club_id: clubId });
      setAccessCreds({ ...data, clubName });
      setForm({ email: "", name: "", cargo: "", role: "coordinator" });
      loadUsers(clubId);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="mt-8 border-2 border-primary/30" data-testid="club-users-card">
      <div className="p-4 border-b border-primary/20 flex items-center gap-2 flex-wrap">
        <Users size={16} className="text-primary" />
        <span className="overline text-primary">🏟 Usuarios de un club · Crear accesos y cargos</span>
      </div>
      <div className="p-4">
        <label className="overline block mb-1">Club</label>
        <select data-testid="club-users-select" value={clubId}
          onChange={(e) => { setClubId(e.target.value); loadUsers(e.target.value); }}
          className="h-10 w-full max-w-sm bg-card border border-border px-2 font-mono text-sm focus:border-primary focus:outline-none">
          <option value="">— Elige un club —</option>
          {clubs.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>

      {clubId && (
        <>
          <div className="divide-y divide-border border-t border-border">
            {loadingU && <div className="p-4 text-sm text-muted-foreground">Cargando usuarios…</div>}
            {!loadingU && users.length === 0 && <div className="p-4 text-sm text-muted-foreground">Este club no tiene usuarios todavía.</div>}
            {users.map((u) => {
              const r = CLUB_ROLE_LABEL[u.role] || { label: u.role, color: "text-muted-foreground border-border" };
              return (
                <div key={u.id} className="flex items-center gap-3 p-3 flex-wrap" data-testid={`club-user-${u.id}`}>
                  <div className="flex-1 min-w-[200px]">
                    <div className="font-heading font-bold uppercase tracking-tight text-sm">
                      {u.name || u.email}
                      {u.cargo && <span className="ml-2 text-[10px] text-amber-400 border border-amber-400/40 px-1.5 py-0.5 tracking-wider">{u.cargo}</span>}
                    </div>
                    <div className="text-xs text-muted-foreground font-mono">{u.email}{u.assigned_teams?.length ? ` · ${u.assigned_teams.map((t) => t.name).join(", ")}` : ""}</div>
                  </div>
                  <span className={`border px-2 py-1 text-[10px] font-heading font-black uppercase tracking-wider ${r.color}`}>{r.label}</span>
                  {u.role !== "familia" && (
                    <button
                      data-testid={`club-user-reset-${u.id}`}
                      onClick={() => provision({ email: u.email, name: u.name, cargo: u.cargo || "", role: u.role })}
                      disabled={busy}
                      className="h-8 px-3 border border-amber-500/40 text-amber-400 text-[10px] uppercase font-heading tracking-widest hover:bg-amber-500/10 disabled:opacity-50"
                    >
                      🔑 Resetear clave
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          <form onSubmit={(e) => { e.preventDefault(); provision(form); }}
            className="p-4 border-t-2 border-primary/20 flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[200px]">
              <label className="overline block mb-1">Email *</label>
              <input data-testid="club-user-email" required type="email" value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="tesorero@club.com"
                className="w-full h-10 bg-card border border-border px-3 font-mono text-sm focus:border-primary focus:outline-none" />
            </div>
            <div className="min-w-[130px]">
              <label className="overline block mb-1">Nombre</label>
              <input data-testid="club-user-name" value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full h-10 bg-card border border-border px-3 font-mono text-sm focus:border-primary focus:outline-none" />
            </div>
            <div className="min-w-[150px]">
              <label className="overline block mb-1">Cargo (trabajo)</label>
              <input data-testid="club-user-cargo" value={form.cargo} list="cargos-sugeridos"
                onChange={(e) => setForm({ ...form, cargo: e.target.value })}
                placeholder="Tesorero, Presidente…"
                className="w-full h-10 bg-card border border-border px-3 font-mono text-sm focus:border-primary focus:outline-none" />
              <datalist id="cargos-sugeridos">
                {CARGO_SUGERIDOS.map((c) => <option key={c} value={c} />)}
              </datalist>
            </div>
            <div>
              <label className="overline block mb-1">Permisos *</label>
              <select data-testid="club-user-role" value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value })}
                className="h-10 bg-card border border-border px-2 font-mono text-xs focus:border-primary focus:outline-none">
                <option value="club_admin">Admin del club (todo + facturación)</option>
                <option value="coordinator">Coordinador (todo el club deportivo)</option>
                <option value="director_deportivo">Director deportivo</option>
                <option value="coach">Entrenador (sus equipos)</option>
                <option value="delegado">Delegado (sus equipos)</option>
              </select>
            </div>
            <button data-testid="club-user-create-btn" disabled={busy}
              className="h-10 px-5 bg-primary text-primary-foreground font-heading font-black text-xs uppercase tracking-widest hover:opacity-90 disabled:opacity-50">
              {busy ? "Creando…" : "➕ Crear / Resetear acceso"}
            </button>
          </form>
          <p className="px-4 pb-4 text-[11px] text-muted-foreground">
            El <b>cargo</b> (Presidente, Tesorero…) es informativo y se muestra junto al usuario; los <b>permisos</b> definen qué puede hacer en la app. La clave se muestra una vez y se envía por email. A entrenadores/delegados se les asignan los equipos desde el panel del club.
          </p>
        </>
      )}
    </div>
  );
}
